import { useEffect, useState } from "react";
import { Loader2, Save, Target } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full px-3 py-2 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";

export default function TravelSettings() {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/travel/settings")
      .then(({ data }) => setValue(String(data.weekly_target_km ?? 0)))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const n = parseFloat(value);
    if (isNaN(n) || n < 0) return toast.error("Enter a valid number of kilometers");
    setSaving(true);
    try {
      await api.put("/travel/settings", { weekly_target_km: n });
      toast.success("Weekly target saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="max-w-xl" data-testid="travel-settings">
      <form onSubmit={save} className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
          <h2 className="font-serif text-xl text-foreground">Weekly driving target</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          How many kilometers do you expect to drive in a typical week? This baseline lets the AI
          spot deviations from your usual travel in the future.
        </p>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Expected kilometers per week</label>
          <div className="flex items-center gap-2 max-w-[220px]">
            <input type="number" min="0" step="1" className={inputCls} value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 250" data-testid="travel-target-input" />
            <span className="text-sm text-muted-foreground">km</span>
          </div>
        </div>
        <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md font-medium text-background bg-foreground hover:bg-foreground/90 disabled:opacity-50 transition-colors duration-200" data-testid="travel-target-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save target
        </button>
      </form>
    </div>
  );
}
